import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { Button, ButtonGroup, Col, Container, Spinner } from "reactstrap";

import { Promotion } from "../../backoffice/dtos/promotions/promotion";
import { useGetPromotionsQuery, useStateChangePromotionsMutation } from "../../backoffice/apis/promotionAPI";
import { getUser } from "../../helpers/localStorage";
import { useSetPromotionId } from "../../app/hooks";
import { useTranslation } from "react-i18next";
import { PromotionCriteria, PromotionCriteriaComparison } from "../../backoffice/dtos/promotions/promotionCriteria";
import CriteriaModal from "../criteria/CriteriaModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCircleInfo, faExclamationCircle, faFileExcel, faFilter, faInfo, faShare, faX, faXmark, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { getMessageFromRtkError } from "../../backoffice/apis/baseAPI";
import { Table } from "../views/common/Table/Table";
import { TableHeader } from "../views/common/Table/TableHeader";
import { TableBody } from "../views/common/Table/TableBody";
import { TableRow } from "../views/common/Table/TableRow";
import { getCanReject, getCanPromote, user } from "../../backoffice/dtos/authentication/user";
import { PromotionProduct } from "../../backoffice/dtos/promotions/promotionProduct";
import { formatToDMY } from "../../helpers/dateHelper";
import { PromotionApprovalModel } from "./models/promotionApprovalModel";
import { confirmAlert } from "react-confirm-alert";
import { PromotionApproveState } from "../../backoffice/dtos/promotions/promotionApproveState";
import { toast } from "react-toastify";
import { downloadExcel } from "../../backoffice/apis/excelAPI";
import { ApprovalStatusColor, EApprovalStatus, getApprovalStatusSymbol } from "../../enums/EApprovalStatus";
import { DashboardGroup } from "./DashboardGroup";
import { TableExpansionContent } from "../views/common/Table/TableExpansionContent";
import { getItemByItem, getItemByPromotions, Item } from "./DashboardItem";

const initialCriteria: PromotionCriteria = { onlyAvailableToPromote: true };
export default function Dashboard() {
    const user = getUser()
    const { t } = useTranslation(['dashboard'])

    const setPromotionId = useSetPromotionId();

    const [criteraModalOpen, setCriteriaModalOpen] = useState(false);
    const [selectedElements, setSelectedElements] = useState<Map<number, { promotion: Promotion, approve: boolean }>>(new Map())

    const [promotionCriteria, setPromotionCriteria] = useState<PromotionCriteria>(initialCriteria)
    const [changeStateOnPromotions] = useStateChangePromotionsMutation()

    const { data: promotions, error: promotionsError, isFetching } = useGetPromotionsQuery(promotionCriteria, { refetchOnMountOrArgChange: true });

    const item = useMemo(() => {
        if (!promotions || promotions.length === 0) {
            return null;
        }

        const map = new Map<string, Map<string, Promotion[]>>();
        for (const promotion of promotions) {
            let customerMaps = map.get(promotion.groupDescription);
            if (!customerMaps) {
                customerMaps = new Map<string, Promotion[]>();
                map.set(promotion.groupDescription, customerMaps);
            }

            let promotions = customerMaps.get(promotion.customerDescription);
            if (!promotions) {
                promotions = [];
                customerMaps.set(promotion.customerDescription, promotions);
            }

            promotions.push(promotion);
        }

        let groups: Item[] = []
        for(const groupDescription of map.keys()) {
            let customerItems: Item[] = []
            let customerValues = map.get(groupDescription)!;
            for(const customerDescription of customerValues.keys()) {
                const promotions = customerValues.get(customerDescription)!;
                let item = getItemByPromotions(customerDescription, t('retail_chains'), promotions);
                customerItems.push(item);
            }
            const groupItem = getItemByItem(groupDescription, t('retail_chains'), customerItems);
            groups.push(groupItem);
        }

        return getItemByItem("", t('groups'), groups);
    }, [promotions]);

    const clickHandler = useCallback((promotionId: number) => {
        setPromotionId(promotionId)
    }, [])

    const submitElements = useCallback(async (promotions: PromotionApprovalModel[]) => {
        try {
            const promotionStates: Array<PromotionApproveState> = [];
            for (const value of promotions) {
                promotionStates.push(
                    {
                        id: value.promotion.id,
                        isPromoted: value.approve
                    }
                )
            }

            if (promotionStates.length > 0) {
                const promotionsToChange = { promotionStates, description: "" };
                const stateChangePromise = await changeStateOnPromotions(promotionsToChange).unwrap();
                toast(t('successful_edit_alert', { success: stateChangePromise.success, total: stateChangePromise.total }))

            }

            setSelectedElements(new Map());
        } catch (e) {
            toast(t('unsuccessful_edit_alert', { error: e }))
        }
    }, [changeStateOnPromotions]);

    const askToSubmit = useCallback((res: PromotionApprovalModel[]) => {
        confirmAlert({
            title: t('submit_confirm_alert_title'),
            message: t('submit_confirm_alert_desctription'),
            buttons: [
                { label: t('yes', { ns: 'common' }), onClick: () => submitElements(res) },
                { label: t('no', { ns: 'common' }), onClick: () => { } }]
        });
    }, [submitElements]);

    const submitSelected = useCallback(() => {
        const res = [...selectedElements.entries()].map(m => m[1]);
        askToSubmit(res)
    }, [selectedElements, askToSubmit])

    let pendingPromotions = <></>;
    if (isFetching) {
        pendingPromotions = (
            <div className="d-flex flex-column flex-fill align-items-center justify-content-center">
                <Spinner />
                <div>
                    <strong>{t('pending_loading')}</strong>
                </div>
            </div>
        );
    } else if (promotionsError) {
        pendingPromotions = (
            <div className="d-flex flex-column flex-fill align-items-center justify-content-center">
                <FontAwesomeIcon icon={faExclamationCircle} size="5x" color="red" />
                <div>
                    <strong>{getMessageFromRtkError(promotionsError)}</strong>
                </div>
            </div>
        );
    } else if (!item) {
        pendingPromotions = (
            <div className="d-flex flex-column flex-fill align-items-center justify-content-center">
                <FontAwesomeIcon icon={faCheckCircle} size="5x" color="green" />
                <div>
                    <strong>{t('no_pending')}</strong>
                </div>
            </div>
        );
    } else {

        pendingPromotions = (
            <>
                <ItemComponent user={user} launchPromotion={clickHandler} item={item} setSelectedElements={setSelectedElements} selectedElements={selectedElements} />
            </>
        );
    }

    return (
        <>
            <Container className="mt-3 p-1 d-flex justify-content-between align-items-center">
                <div>
                    <ButtonGroup>
                        <Button onClick={() => setCriteriaModalOpen(prev => !prev)}>
                            <FontAwesomeIcon icon={faFilter} />
                        </Button>
                        {!PromotionCriteriaComparison(promotionCriteria, initialCriteria) && (
                            <Button color="danger" onClick={() => setPromotionCriteria(initialCriteria)}><FontAwesomeIcon icon={faXmark} /></Button>
                        )}
                    </ButtonGroup>{" "}
                    <Button color="success" onClick={async () => await downloadExcel(promotionCriteria)}>
                        <FontAwesomeIcon icon={faFileExcel} />
                    </Button>
                </div>

                <ButtonGroup>
                    <Button color="primary" onClick={() => submitSelected()} disabled={selectedElements.size === 0}>
                        {t('submit', { ns: 'common' })}
                    </Button>
                    {selectedElements.size > 0 && (
                        <Button color="danger" onClick={() => setSelectedElements(new Map())}><FontAwesomeIcon icon={faXmark} /></Button>
                    )}
                </ButtonGroup>
            </Container>
            <div className="d-flex flex-column flex-fill">

                {pendingPromotions}

                <CriteriaModal isOpen={criteraModalOpen} toggle={() => setCriteriaModalOpen(prev => !prev)} currentFilter={promotionCriteria} setSelectedPromotionCriteria={setPromotionCriteria} initialFilter={initialCriteria} />
            </div>
        </>
    )

}

interface ItemAttributes extends React.HTMLAttributes<HTMLElement> {
    user: user | undefined;
    item: Item;
    launchPromotion: (promotionId: number) => void;
    selectedElements: Map<number, { promotion: Promotion, approve: boolean }>;
    setSelectedElements: Dispatch<SetStateAction<Map<number, { promotion: Promotion, approve: boolean }>>>;
}
function ItemComponent(
    props: ItemAttributes
) {
    const { t } = useTranslation(['dashboard'])

    return (
        <>
            {
                props.item.isItem() ? (
                    <>
                        <Table className="mt-2 shadow">
                            <TableHeader className="bold first-layer-header">
                                <Col>{t(props.item.itemDescription)}</Col>
                                <Col className="p-0 ps-1 invisible" md="auto">
                                    <Button className="w-100">
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                    </Button>
                                </Col>
                                <Col className="p-0 ps-1 invisible" md="auto">
                                    <Button className="w-100">
                                        <FontAwesomeIcon icon={faXmarkCircle} />
                                    </Button>
                                </Col>
                            </TableHeader>
                            <TableBody>
                                {
                                    props.item.item!.map((i) => (
                                        <ItemRow user={props.user} item={i} launchPromotion={props.launchPromotion} selectedElements={props.selectedElements} setSelectedElements={props.setSelectedElements} />
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </>
                ) : (
                        <>
                            <Table className="mt-2 shadow">
                                <TableHeader className="bold second-layer-header">
                                    <Col md="auto"><FontAwesomeIcon className="icon" icon={faCircleInfo} /></Col>
                                    <Col md={1}>{t("TPM")}</Col>
                                    <Col md={0}>{t("ERP")}</Col>
                                    <Col md={3}>{t("duration")}</Col>
                                    <Col className="text-align-end" md={0}>{t("estimate_price")}</Col>
                                    <Col className="text-align-end" md={0}>{t("actual_price")}</Col>
                                    <Col className="text-align-end" md={0}>{t("mediafee")}</Col>
                                    <Col className="p-0 ps-1 invisible" md="auto">
                                        <Button className="w-100" color="success">
                                            <FontAwesomeIcon icon={faShare} />
                                        </Button>
                                    </Col>
                                    <Col className="p-0 ps-1 invisible" md="auto">
                                        <Button className="w-100" color="success">
                                            <FontAwesomeIcon icon={faXmarkCircle} />
                                        </Button>
                                    </Col>
                                    <Col className="p-0 ps-1 invisible" md="auto">
                                        <Button className="w-100" color="danger">
                                            <FontAwesomeIcon icon={faXmarkCircle} />
                                        </Button>
                                    </Col>
                                </TableHeader>
                                <TableBody>
                                    {
                                        [...props.item.promotions!]
                                        .sort((a, b) => a.approvalStatus - b.approvalStatus)
                                        .map(promotion =>
                                        {
                                                return (
                                                    <PromotionRow
                                                        user={props.user}
                                                        promotion={promotion}
                                                        selectedElements={props.selectedElements}
                                                        setSelectedElements={props.setSelectedElements}
                                                        launchPromotion={props.launchPromotion}
                                                    />
                                                )
                                            }
                                        )
                                    }
                                </TableBody>
                            </Table>
                        </>
                    )
            }
        </>
    );
}

function ItemRow(props: ItemAttributes) {
    const { t } = useTranslation(['dashboard'])

    const canApprove = useMemo(() => {
        return props.item.canPromote(props.user);
    }, [props.item, props.user]);

    const approveSelected = useMemo(() => {
        return props.item.getApproveSelected(props.user, props.selectedElements);
    }, [props.item, props.selectedElements, props.user]);

    const rejectSelected = useMemo(() => {
        return props.item.getRejectSelected(props.user, props.selectedElements);
    }, [props.item, props.selectedElements, props.user]);

    const approvalFlow = useCallback((e: any, item: Item) => {
        e.preventDefault();
        e.stopPropagation();

        item.approvalFlow(props.user, props.selectedElements, props.setSelectedElements);
    }, [props.user, props.selectedElements, props.setSelectedElements])

    const rejectFlow = useCallback((e: any, item: Item) => {
        e.preventDefault();
        e.stopPropagation();

        item.rejectFlow(props.user, props.selectedElements, props.setSelectedElements);
    }, [props.user, props.item, props.selectedElements, props.setSelectedElements])

    return (
        <TableRow>
            <Col>{props.item.description}</Col>
            <Col className={["p-0 ps-1", !canApprove || approveSelected === null ? "invisible" : ""].join(" ")} md="auto">
                <Button outline={!approveSelected} className="w-100" color="success" onClick={(e) => approvalFlow(e, props.item)} title={t("select_companys_promotions_for_approval")}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                </Button>
            </Col>
            <Col className={["p-0 ps-1", !canApprove || rejectSelected === null ? "invisible" : ""].join(" ")} md="auto">
                <Button outline={!rejectSelected} className="w-100" color="danger" onClick={(e) => rejectFlow(e, props.item)} title={t("select_companys_promotions_for_rejection")}>
                    <FontAwesomeIcon icon={faXmarkCircle} />
                </Button>
            </Col>
            <TableExpansionContent>
                <ItemComponent item={props.item} user={props.user} launchPromotion={props.launchPromotion} selectedElements={props.selectedElements} setSelectedElements={props.setSelectedElements} />
            </TableExpansionContent>
        </TableRow>
    );
}

interface PromotionRowAttributes extends React.HTMLAttributes<HTMLElement> {
    user: user | undefined;
    promotion: Promotion
    launchPromotion: (promotionId: number) => void
    selectedElements: Map<number, { promotion: Promotion, approve: boolean }>
    setSelectedElements: Dispatch<SetStateAction<Map<number, { promotion: Promotion, approve: boolean }>>>
}
function PromotionRow(props: PromotionRowAttributes) {
    const { t } = useTranslation(['dashboard']);

    const launchProduct = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();
        props.launchPromotion(props.promotion.id);
    }, [props.launchPromotion, props.promotion])

    const approveSelected = useMemo(() => {
        return props.selectedElements.get(props.promotion.id)?.approve === true;
    }, [props.selectedElements])

    const rejectSelected = useMemo(() => {
        return props.selectedElements.get(props.promotion.id)?.approve === false;
    }, [props.selectedElements])

    const [userCanApprove, approvalFlow] = useMemo(() => {
        const promotion = props.promotion;
        if (!props.user || !getCanPromote(props.user, props.promotion))
            return [false, (e: any) => {
                e.preventDefault();
                e.stopPropagation();
            }]

        const action = (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            const promotionPrev = props.selectedElements.get(promotion.id);
            if (promotionPrev?.approve === true) {
                props.selectedElements.delete(promotion.id)
            } else {
                props.selectedElements.set(promotion.id, {
                    approve: true,
                    promotion: promotion
                });
            }
            const newElements = new Map(props.selectedElements);
            props.setSelectedElements(newElements);
        }
        return [true, action]
    }, [props.user, props.promotion, props.selectedElements, props.setSelectedElements])

    const [userCanReject, rejectFlow] = useMemo(() => {
        const promotion = props.promotion;
        if (!props.user || !getCanReject(props.user, props.promotion))
            return [false, (e: any) => {
                e.preventDefault();
                e.stopPropagation();
            }]

        const action = (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            const promotionPrev = props.selectedElements.get(promotion.id);
            if (promotionPrev?.approve === false) {
                props.selectedElements.delete(promotion.id)
            } else {
                props.selectedElements.set(promotion.id, {
                    approve: false,
                    promotion: promotion
                });
            }
            const newElements = new Map(props.selectedElements);
            props.setSelectedElements(newElements);
        }
        return [true, action]
    }, [props.user, props.promotion, props.selectedElements, props.setSelectedElements])
    const statusIcon = useMemo(() => getApprovalStatusSymbol(props.promotion.approvalStatus), [props.promotion.approvalStatus])

    return (
        <TableRow>
            <Col md="auto"><FontAwesomeIcon className="icon" icon={statusIcon ?? faExclamationCircle} style={{ color: ApprovalStatusColor[props.promotion.approvalStatus as EApprovalStatus] }} /></Col>
            <Col md={1}>{props.promotion.id}</Col>
            <Col className="text-nowrap overflow-scroll" md={0}>{props.promotion.poNumber}</Col>
            <Col md={3}>{formatToDMY(new Date(props.promotion.fromDate), '/')} - {formatToDMY(new Date(props.promotion.toDate), '/')}</Col>
            <Col className="text-align-end" md={0}>{props.promotion.promotionProducts.map(m => m.euroEstimate).reduce((a, b) => a + b, 0).toFixed(2)}</Col>
            <Col className="text-align-end" md={0}>{props.promotion.promotionProducts.map(m => m.sellOutEuro).reduce((a, b) => a + b, 0).toFixed(2)}</Col>
            <Col className="text-align-end" md={0}>{props.promotion.mediaFee}</Col>
            <Col className="p-0 ps-1" md="auto">
                <Button className="w-100" color="primary" onClick={launchProduct}>
                    <FontAwesomeIcon icon={faShare} />
                </Button>
            </Col>
            <Col className={["p-0", "ps-1", !userCanApprove ? "invisible" : ""].join(" ")} md="auto">
                <Button outline={!approveSelected} className="w-100" color="success" onClick={approvalFlow}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                </Button>
            </Col>
            <Col className={["p-0", "ps-1", !userCanReject ? "invisible" : ""].join(" ")} md="auto">
                <Button outline={!rejectSelected} className="w-100" color="danger" onClick={rejectFlow}>
                    <FontAwesomeIcon icon={faXmarkCircle} />
                </Button>
            </Col>
            <TableExpansionContent>
                <Table>
                    <TableHeader className="bold third-layer-header">
                        <Col md={1}>
                            {t("code")}
                        </Col>
                        <Col md={5}>
                            {t("description")}
                        </Col>
                        <Col>
                            {t("discountType")}
                        </Col>
                        <Col className="text-align-end">
                            {t("estimate_price")}
                        </Col>
                        <Col className="text-align-end">
                            {t("actual_price")}
                        </Col>
                    </TableHeader>
                    <TableBody className="mb-4">
                        {props.promotion.promotionProducts.map(pp =>
                            <ProductRow promotionProduct={pp} />)}
                    </TableBody>
                </Table>
            </TableExpansionContent>
        </TableRow>
    )
}

interface ProductRowAttributes extends React.HTMLAttributes<HTMLElement> {
    promotionProduct: PromotionProduct
}
function ProductRow(props: ProductRowAttributes) {
    const { t } = useTranslation(['dashboard']);
    return (
        <TableRow>
            <Col md={1}>{parseInt(props.promotionProduct.productCode)}</Col>
            <Col className="text-nowrap overflow-scroll" md={5}><div>{props.promotionProduct.productDescription}</div></Col>
            <Col md={0}>{props.promotionProduct.discountTypeDescription}</Col>
            <Col className="text-align-end" md={0}>{props.promotionProduct.euroEstimate}</Col>
            <Col className="text-align-end" md={0}>{props.promotionProduct.sellOutEuro}</Col>
        </TableRow>
    )
}
